// src/shared/components/SortableTh.tsx
//
// Clickable Mantine table header cell wired to useTableSort. Non-sortable columns
// should keep a plain <Table.Th>; use this only for sortable ones.

import { Table } from '@mantine/core';
import type { ReactNode } from 'react';
import type { SortDirection } from '@/shared/hooks/useTableSort';

interface SortableThProps<K extends string> {
  columnKey: K;
  sortKey: K;
  sortDir: SortDirection;
  onSort: (key: K) => void;
  children: ReactNode;
  /** Header text alignment; use 'right' for numeric columns. */
  ta?: 'left' | 'center' | 'right';
  w?: number | string;
}

export function SortableTh<K extends string>({
  columnKey,
  sortKey,
  sortDir,
  onSort,
  children,
  ta = 'left',
  w,
}: SortableThProps<K>) {
  const active = sortKey === columnKey;
  const arrow = active ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  return (
    <Table.Th
      w={w}
      onClick={() => onSort(columnKey)}
      aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
      style={{ cursor: 'pointer', textAlign: ta, userSelect: 'none', whiteSpace: 'nowrap' }}
    >
      {children}
      {arrow}
    </Table.Th>
  );
}
